import { ShowMore } from '@re-dev/react-truncate'
import { BarChart4Icon, FlagIcon, LinkIcon, LinkedinIcon, UsersIcon } from 'lucide-react'

import { CompanyProfile as ProtoCompanyProfile } from '@/gen/inventory/v1/company_service_pb'

import { cn } from '@/lib/ui/style-helpers'

import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'

export type CompanyProfileProps = {
  info: ProtoCompanyProfile
}

export const CompanyProfile = ({ info }: CompanyProfileProps) => {
  return (
    <div className='flex flex-col gap-4'>
      <ShowMore more='Show more' less='Show less' lines={4}>
        {info.paragraphDescription}
      </ShowMore>
      <div className='flex max-w-4xl flex-wrap justify-items-stretch gap-2'>
        <CompanyStatisticItem title='Industry' disabled={!info.industrySector}>
          <span className='line-clamp-1 capitalize'>{info.industrySector}</span>
        </CompanyStatisticItem>
        <CompanyStatisticItem
          title='Website'
          icon={<LinkIcon size={10} />}
          disabled={!info.website}
        >
          <Hyperlink href={`https://${info.website}`}>{info.website}</Hyperlink>
        </CompanyStatisticItem>
        <CompanyStatisticItem
          title='Linkedin'
          icon={<LinkedinIcon size={10} />}
          disabled={!info.linkedinUrl}
        >
          <Hyperlink className='line-clamp-1' href={`https://${info.linkedinUrl}`}>
            {info.name}
          </Hyperlink>
        </CompanyStatisticItem>
        <CompanyStatisticItem
          title='Size'
          icon={<UsersIcon size={10} />}
          disabled={!info.companySize}
        >
          {info.companySize}
        </CompanyStatisticItem>
        <CompanyStatisticItem
          title='Founded'
          icon={<FlagIcon size={10} />}
          disabled={!info.foundingYear}
        >
          {info.foundingYear}
        </CompanyStatisticItem>

        <CompanyStatisticItem
          title='Stocks'
          icon={<BarChart4Icon size={10} />}
          disabled={!info.ticker}
        >
          <Hyperlink href={`https://finance.yahoo.com/quote/${info.ticker}`}>
            {info.ticker}
          </Hyperlink>
        </CompanyStatisticItem>
      </div>
    </div>
  )
}

const CompanyStatisticItem = ({
  title,
  icon,
  children,
  disabled,
}: {
  title: string
  icon?: React.ReactNode
  children?: React.ReactNode
  disabled?: boolean
}) => {
  return (
    <div
      className={cn(
        'flex w-36 flex-col items-start gap-1 p-2',
        disabled && 'select-none opacity-60',
      )}
    >
      <div className='flex flex-row items-center'>
        {icon && <span className='mr-1'>{icon}</span>}
        <h3 className='text-xs font-bold tracking-widest text-main-foreground-tertiary'>{title}</h3>
      </div>
      <div className='line-clamp-1 break-all'>
        {!disabled && children}
        {disabled && <>N/A</>}
      </div>
    </div>
  )
}
